import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthActions } from "@convex-dev/auth/react";
import { AuthForm, InputField } from "../components/AuthForm";

export function SignUpPage() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Check if signups are enabled via environment variable
  const signupsEnabled = import.meta.env.VITE_ENABLE_SIGNUPS === "true";

  // Redirect to sign in if signups are disabled
  useEffect(() => {
    if (!signupsEnabled) {
      navigate("/signin");
    }
  }, [signupsEnabled, navigate]);

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await signIn("password", {
        email,
        password,
        displayName,
        flow: "signUp",
      });
      // Successfully signed up, redirect to home
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Failed to sign up. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if signups are disabled (will redirect)
  if (!signupsEnabled) {
    return null;
  }

  return (
    <AuthForm
      title="Create Account"
      onSubmit={handleSignUp}
      submitLabel="Sign Up"
      isLoading={isLoading}
      error={error}
      footer={
        <p>
          Already have an account?{" "}
          <Link to="/signin" className="text-blue-600 hover:underline">
            Sign In
          </Link>
        </p>
      }
    >
      <InputField
        label="Display Name"
        type="text"
        value={displayName}
        onChange={setDisplayName}
        placeholder="Enter your name"
        autoComplete="name"
      />

      <InputField
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="Enter your email"
        autoComplete="email"
      />

      <InputField
        label="Password"
        type="password"
        value={password}
        onChange={setPassword}
        placeholder="Create a password (min 8 characters)"
        autoComplete="new-password"
      />
    </AuthForm>
  );
}
